/**
 * Focus trap + escape-to-close + aria-modal helper for form modals.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useModalA11y(ref, open, onClose);
 *   <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="my-title">
 *
 * IMPORTANT — Focus stability (P0 bug fix):
 *   The initial auto-focus MUST NOT depend on `onClose`. Consumers often pass
 *   an inline arrow (`() => tryClose()`) whose identity changes on every
 *   parent render. If auto-focus lived in the same effect as the keydown
 *   listener, every keystroke that updated the form state would re-run the
 *   effect and yank the focus back to the first input — making textareas
 *   accept only one character at a time. We split into two effects.
 */
import { useEffect } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function useModalA11y(ref, open, onClose) {
  // Effect A — Auto-focus the preferred field ONCE per open transition.
  // Depends only on [open, ref] so re-renders driven by unrelated state
  // (typing in the textarea, toggles, etc.) never steal focus back.
  useEffect(() => {
    if (!open || !ref.current) return;
    const container = ref.current;
    const focusables = container.querySelectorAll(FOCUSABLE);
    const first = focusables[0];
    if (!first) return;
    if (document.activeElement && container.contains(document.activeElement)) {
      // User is already interacting with a field inside the modal — do not
      // hijack their focus.
      return;
    }
    const preferred = container.querySelector(
      'input[type="text"], input:not([type]), textarea, input[type="email"], input[type="number"]'
    );
    (preferred || first).focus();
  }, [open, ref]);

  // Effect B — Escape-to-close + tab-trap keydown listener.
  // May re-attach when `onClose` identity changes; it's cheap and does NOT
  // touch focus, so no visible side-effect on the user.
  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const container = ref.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE))
        .filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault(); lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault(); firstEl.focus();
      }
    };
    container.addEventListener("keydown", onKeyDown);
    return () => container.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, ref]);
}
